import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import NewMemberCourtReservations from './MemberCourtReservations';
import { logger } from '../shared/logger';
import { format, parseISO } from 'date-fns';
import MemberEvents from './MemberEvents';
import NewMemberSubscription from './MemberSubscription';
import MemberLessons from './MemberLessons';
import MemberSettings from './MemberSettings';
import PPU_Reservation from './PPU_Reservation';
import { 
  HomeIcon, 
  UserCircleIcon,
  CalendarIcon, 
  UserGroupIcon, 
  XCircleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ArrowRightOnRectangleIcon,
  Cog6ToothIcon,
  AcademicCapIcon,
  CheckCircleIcon,
  WrenchScrewdriverIcon
} from '@heroicons/react/24/outline';
import RepeatIcon from '@mui/icons-material/Repeat';
import { getFacilityBusinessHours, facilityApi, guestApi, memberApi, ppuApi, recurringEventApi } from '../api';
import { reservationApi } from '../reservation_api';
import { BusinessHours, Facility as ApiFacility, PublicFacility, UpcomingReservation, AvailableTimeSlot, EventData, MemberData } from '../api_types';
import { websocketService } from '../services/websocket';
import StringingService from './StringingService';
import LoadingSpinner from '../shared/components/LoadingSpinner';

interface RecurringEventData {
  id: string;
  title: string;
  description: string;
  startDate: string;
  endDate?: string;
  startTime: string;
  endTime: string;
  courtIds: string[];
  capacity?: number;
  recurrenceRule?: {
    frequency: string;
    interval?: number;
    endDate?: string;
    daysOfWeek?: number[];
  };
  status: string;
  participants?: {
    userId: string;
    signUpDate: string;
    name?: string;
    participantId?: string;
  }[];
  isCurrentUserSignedUp?: boolean;
}

const DURATION_OPTIONS = [
  { label: '30 min', value: '30min' },
  { label: '1 hour', value: '1hour' }
];

const MemberDashboard: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [facilityId, setFacilityId] = useState<string | null>(null);
  const [businessId, setBusinessId] = useState<string | null>(null);
  const [facilityName, setFacilityName] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [memberData, setMemberData] = useState<MemberData | null>(null);
  const [upcomingReservations, setUpcomingReservations] = useState<UpcomingReservation[]>([]);
  const [userName, setUserName] = useState<string>('');
  const [availableTimeSlots, setAvailableTimeSlots] = useState<AvailableTimeSlot[]>([]);
  const [loadingSlots, setLoadingSlots] = useState(true);
  const [upcomingEvents, setUpcomingEvents] = useState<EventData[]>([]);
  const [recurringEvents, setRecurringEvents] = useState<RecurringEventData[]>([]);
  const [loadingEvents, setLoadingEvents] = useState(true);
  const [loadingRecurringEvents, setLoadingRecurringEvents] = useState(true);
  const [quickReserveDuration, setQuickReserveDuration] = useState<'30min' | '1hour'>('1hour');
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [notification, setNotification] = useState<{message: string, type: 'success' | 'error' | null}>({message: '', type: null});
  const [userFacilityId, setUserFacilityId] = useState<string | null>(null);
  const [businessHours, setBusinessHours] = useState<BusinessHours | null>(null);
  const [isPPU, setIsPPU] = useState<boolean>(false);
  const [facility, setFacility] = useState<ApiFacility | null>(null);
  const [publicFacility, setPublicFacility] = useState<PublicFacility | null>(null);
  const [checkingPaymentStatus, setCheckingPaymentStatus] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Single initialization effect to handle all initial data loading
  useEffect(() => {
    const initializeDashboard = async () => {
      if (!user) {
        setError('No user found');
        setLoading(false);
        logger.error('No user found in MemberDashboard', {
          location: window.location.href,
          authToken: localStorage.getItem('authToken'),
          user: null
        });
        return;
      }

      const businessId = localStorage.getItem('businessId');
      const facilityId = localStorage.getItem('facilityId');
      setBusinessId(businessId);
      setFacilityId(facilityId);

      if (!businessId || !facilityId) {
        setLoading(false);
        return;
      }

      try {         
        // Use the facility and business id in conjunction with user id to get the information required
        const memberData = await memberApi.getMemberData(user.uid, businessId, facilityId);
        const facilityData = await facilityApi.getFacility(facilityId, businessId);
        setMemberData(memberData);
        setFacility(facilityData);

        setUserName(`${memberData.firstName} ${memberData.lastName}`.trim() || 'Member');
        setFacilityName(facilityData.name);

        // Set PPU status and fetch public facility data if needed
        const isPPUFacility = facilityData.isPPU === true;
        setIsPPU(isPPUFacility);
        
        if (isPPUFacility) {
          const publicFacilityData = await guestApi.getFacilityDetails(businessId, facilityId);
          setPublicFacility(publicFacilityData);
        }

        const businessHours = await getFacilityBusinessHours(businessId, facilityId);
        setBusinessHours(businessHours);
  
        const [ reservations, slots, events, recurringEvents ] = await Promise.all([
          memberApi.getMemberReservations(user.uid, businessId, facilityId),
          memberApi.getAvailableSlots(user.uid, businessId, quickReserveDuration, 3, facilityId),
          memberApi.getMemberEvents(user.uid, businessId, facilityId),
          recurringEventApi.getRecurringEvents(businessId, facilityId)
        ]);

        setUpcomingReservations(reservations);
        setAvailableTimeSlots(slots);
        setUpcomingEvents(events);
        setRecurringEvents(recurringEvents);
      } catch (err) {
        console.error('Debug: Error in initializeDashboard:', err);
        logger.error('Error initializing dashboard:', {
          error: err instanceof Error ? err.message : String(err),
          user,
          location: window.location.href,
          authToken: localStorage.getItem('authToken')
        });
        setError('Failed to initialize dashboard');
      } finally {
        console.log('Debug: Finishing initializeDashboard');
        setLoading(false);
        setLoadingSlots(false);
        setLoadingEvents(false);
        setLoadingRecurringEvents(false);
      }
    };

    initializeDashboard();
  }, [user, quickReserveDuration, facilityId, businessId]);

  // WebSocket setup for real-time updates
  useEffect(() => {
    if (user && userFacilityId) {
      websocketService.connect(userFacilityId, 'member-bookings');
      const handleCourtUpdate = (data: any) => {
        setUpcomingReservations(prev => [...prev, data.reservation]);
      };
      websocketService.subscribe('court_update', handleCourtUpdate);
      return () => {
        websocketService.unsubscribe('court_update', handleCourtUpdate);
        websocketService.disconnect();
      };
    }
  }, [user, userFacilityId, businessId]);

  // Check payment status when returning from Stripe
  useEffect(() => {
    const checkPaymentStatus = async () => {
      const params = new URLSearchParams(location.search);
      const bookingId = params.get('booking_id');
      const sessionId = params.get('session_id');

      if (bookingId && sessionId && businessId && facilityId) {
        setCheckingPaymentStatus(true);
        try {
          const response = await ppuApi.getBookingStatus(bookingId, businessId, facilityId);
          if (response.status === 'confirmed') {
            // Clear URL parameters
            navigate(location.pathname, { replace: true });
            // Show success message
            setSuccessMessage('Reservation confirmed successfully!');
          } else if (response.status === 'pending') {
            setError('Payment is still processing. Please wait a moment and refresh the page.');
          } else {
            setError('Payment failed. Please try again.');
          }
        } catch (err: any) {
          console.error('Error checking payment status:', err);
          setError(err.message || 'Failed to check payment status');
        } finally {
          setCheckingPaymentStatus(false);
        }
      }
    };

    checkPaymentStatus();
  }, [location.search, navigate, businessId, facilityId]);

  const handleLogout = () => {
    navigate('/member/login');
    localStorage.removeItem('authToken');
  };

  const getTimeOfDay = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'morning';
    if (hour < 17) return 'afternoon';
    return 'evening';
  };

  const handleQuickReserve = async (slot: AvailableTimeSlot) => {
    if (!user || !userFacilityId || !businessId) return;

    try {
      const [date, time] = slot.startTime.split('T');
      const [endDate, endTime] = slot.endTime.split('T');

      const assignedUserDetails = {
        id: user.uid,
        name: user.name || user.email || '',
        email: user.email || '',
        role: user.role || 'member',
        type: 'member',
      };

      const reservationData = {
        court_id: slot.courtId,
        date,
        start_time: time,
        end_time: endTime,
        status: 'booked',
        notes: 'Quick reservation from dashboard',
        assigned_to: user.uid,
        assigned_user_details: assignedUserDetails,
      };

      await reservationApi.createReservation(businessId, userFacilityId, reservationData);

      // Show success notification
      setNotification({
        message: `Court ${slot.courtName} reserved for ${formatDateTime(date, time)}`,
        type: 'success'
      });

      // Auto-dismiss notification after 5 seconds
      setTimeout(() => {
        setNotification({message: '', type: null});
      }, 5000);

      // Refresh the available slots and upcoming reservations
      navigate('/member/dashboard/reservations');
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to make reservation');
      logger.error('Error making reservation:', error);

      // Show error notification
      setNotification({
        message: 'Failed to make reservation. Please try again.',
        type: 'error'
      });

      // Auto-dismiss notification after 5 seconds
      setTimeout(() => {
        setNotification({message: '', type: null});
      }, 5000);
    }
  };

  const getActiveSection = () => {
    const path = location.pathname;
    if (path.includes('/subscription')) return 'subscription';
    if (path.includes('/reservations')) return 'reservations';
    if (path.includes('/events')) return 'events';
    if (path.includes('/lessons')) return 'lessons';
    if (path.includes('/stringing')) return 'stringing';
    if (path.includes('/settings')) return 'settings';
    return 'welcome';
  };

  const navigationItems = [
    { 
      name: 'Dashboard', 
      href: '/member/dashboard', 
      icon: HomeIcon,
      section: 'welcome' as const
    },
    { 
      name: 'My Membership', 
      href: '/member/dashboard/subscription', 
      icon: UserCircleIcon,
      section: 'subscription' as const,
      hideForPPU: true
    },
    { 
      name: 'Court Reservations', 
      href: '/member/dashboard/reservations', 
      icon: CalendarIcon,
      section: 'reservations' as const
    },
    { 
      name: 'Events', 
      href: '/member/dashboard/events', 
      icon: UserGroupIcon,
      section: 'events' as const
    },
    { 
      name: 'Lessons and Classes', 
      href: '/member/dashboard/lessons', 
      icon: AcademicCapIcon,
      section: 'lessons' as const
    },
    { 
      name: 'Stringing Service', 
      href: '/member/dashboard/stringing', 
      icon: WrenchScrewdriverIcon,
      section: 'stringing' as const
    },
    { 
      name: 'Settings', 
      href: '/member/dashboard/settings', 
      icon: Cog6ToothIcon,
      section: 'settings' as const
    },
  ];

  // Helper to check if a slot is within business hours
  const isSlotWithinBusinessHours = (slot: AvailableTimeSlot) => {
    if (!businessHours) return true;
    // Assume slot.startTime is ISO string: 'YYYY-MM-DDTHH:mm:ss'
    const [dateStr, startTimeStr] = slot.startTime.split('T');
    const [endDateStr, endTimeStr] = slot.endTime.split('T');
    const dayOfWeek = new Date(dateStr).toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
    const hours = businessHours[dayOfWeek];
    if (!hours || hours.isClosed) return false;
    // Compare times as minutes
    const toMinutes = (t: string) => {
      const [h, m] = t.split(':').map(Number);
      return h * 60 + m;
    };
    const slotStart = toMinutes(startTimeStr);
    const slotEnd = toMinutes(endTimeStr);
    const open = toMinutes(hours.openTime);
    const close = toMinutes(hours.closeTime);
    return slotStart >= open && slotEnd <= close;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-xl">Loading...</div>
      </div>
    );
  }

  if (error) {
    logger.error('MemberDashboard error', {
      error,
      user,
      location: window.location.href,
      authToken: localStorage.getItem('authToken'),
    });
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-xl text-red-500">{error}</div>
      </div>
    );
  }

  if (!businessId || !facilityId) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-xl">No business or facility assigned</div>
      </div>
    );
  }

  const formatDate = (dateStr: string) => {
    return format(parseISO(dateStr), 'MMMM d, yyyy');
  };

  const formatTime = (timeStr: string) => {
    return format(new Date(`2000-01-01T${timeStr}`), 'h:mm a');
  };

  const formatDateTime = (dateStr: string, timeStr: string) => {
    return format(parseISO(`${dateStr}T${timeStr}`), 'MMMM d, yyyy h:mm a');
  };

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  const renderRecurrenceInfo = (event: RecurringEventData) => {
    const { recurrenceRule } = event;
    if (!recurrenceRule) return null;

    const frequencyText = {
      daily: 'Daily',
      weekly: 'Weekly',
      monthly: 'Monthly'
    }[recurrenceRule.frequency] || '';

    const intervalText = recurrenceRule.interval && recurrenceRule.interval > 1
      ? ` (every ${recurrenceRule.interval} ${recurrenceRule.frequency.slice(0, -2)}s)`
      : '';

    const endDateText = recurrenceRule.endDate
      ? ` until ${format(parseISO(recurrenceRule.endDate), 'MMMM d, yyyy')}`
      : '';

    return (
      <p className="text-sm text-gray-500 flex items-center">
        <RepeatIcon style={{ width: '16px', height: '16px', marginRight: '4px' }} />
        <span>
          {frequencyText}
          {intervalText}
        </span>
        {endDateText && <span className="ml-2">{endDateText}</span>}
      </p>
    );
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {checkingPaymentStatus && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-xl">
            <LoadingSpinner size="lg" />
            <div className="mt-4 text-gray-600">
              Verifying payment status...
            </div>
          </div>
        </div>
      )}
      
      {notification.type && (
        <div className={`fixed top-4 left-1/2 transform -translate-x-1/2 z-50 ${
          notification.type === 'success' ? 'bg-green-50' : 'bg-red-50'
        } rounded-md p-4 w-full max-w-md shadow-sm transition-all duration-300`}>
          <p className={`text-sm ${notification.type === 'success' ? 'text-green-700' : 'text-red-700'}`}>
            {notification.type === 'success' && (
              <CheckCircleIcon className="h-5 w-5 inline-block mr-1.5 -mt-0.5" aria-hidden="true" />
            )}
            {notification.type === 'error' && (
              <XCircleIcon className="h-5 w-5 inline-block mr-1.5 -mt-0.5" aria-hidden="true" />
            )}
            {notification.message}
          </p>
        </div>
      )}
      
      <div className="flex">
        {/* Sidebar Navigation */}
        <div className={`${isSidebarCollapsed ? 'w-16' : 'w-64'} bg-white shadow-sm h-screen fixed transition-all duration-300`}>
          <div className="flex flex-col h-full">
            <div className="flex-shrink-0 px-4 py-4 border-b">
              <div className="flex items-center justify-between">
                {!isSidebarCollapsed && (
                  <h1 className="text-xl font-bold text-gray-900">Member Portal</h1>
                )}
                <button
                  onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
                  className="p-2 rounded-md hover:bg-gray-100"
                >
                  {isSidebarCollapsed ? (
                    <ChevronRightIcon className="h-5 w-5 text-gray-500" />
                  ) : (
                    <ChevronLeftIcon className="h-5 w-5 text-gray-500" />
                  )}
                </button>
              </div>
              {!isSidebarCollapsed && facilityName && (
                <div className="mt-3 flex items-start space-x-2 px-1">
                  <svg className="h-4 w-4 mt-0.5 flex-shrink-0 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-900 break-words leading-5">
                      {facilityName}
                    </p>
                  </div>
                </div>
              )}
            </div>
            <nav className="flex-1 px-2 py-4 space-y-1">
              {navigationItems
                .filter(item => !item.hideForPPU || !isPPU)
                .map((item) => {
                  const Icon = item.icon;
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={`${
                        getActiveSection() === item.section
                          ? 'bg-indigo-50 text-indigo-600 border-indigo-500'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900 border-transparent'
                      } group flex items-center px-3 py-2 text-sm font-medium rounded-md border-l-2`}
                    >
                      <Icon className={`${isSidebarCollapsed ? 'mx-auto' : 'mr-3'} h-5 w-5 flex-shrink-0`} />
                      {!isSidebarCollapsed && item.name}
                    </Link>
                  );
                })}
            </nav>
            <div className="flex-shrink-0 px-4 py-4 border-t">
              <button 
                onClick={handleLogout}
                className={`w-full flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-gray-700 bg-gray-100 hover:bg-gray-200 ${
                  isSidebarCollapsed ? 'px-2' : ''
                }`}
              >
                {isSidebarCollapsed ? (
                  <ArrowRightOnRectangleIcon className="h-5 w-5" />
                ) : (
                  'Logout'
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className={`flex-1 transition-all duration-300 ${isSidebarCollapsed ? 'ml-16' : 'ml-64'} bg-white min-h-screen flex flex-col`}>
          <main className="flex-1 flex flex-col">
            <div className="flex-1 flex flex-col">
              <div className="flex-1 flex flex-col">
                {getActiveSection() === 'welcome' && (
                  <div className="bg-white overflow-hidden shadow rounded-lg flex-1 flex flex-col">
                    <div className="p-6">
                      <div className="flex justify-between items-center mb-6">
                        <h2 className="text-2xl font-bold text-gray-900">Welcome Back, {userName}!</h2>
                        <Link
                          to="/member/dashboard/reservations"
                          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                        >
                          <CalendarIcon className="h-5 w-5 mr-2" />
                          Quick Court Reservation
                        </Link>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Left Column */}
                        <div className="space-y-6">
                          {/* Upcoming Court Reservations */}
                          <div className="bg-gray-50 p-4 rounded-lg">
                            <h3 className="text-lg font-medium text-gray-900 mb-4">Your Upcoming Court Reservations</h3>
                            {upcomingReservations.length > 0 ? (
                              <div className="space-y-4">
                                {upcomingReservations.map((reservation) => (
                                  <div key={reservation.id} className="bg-white p-4 rounded-lg shadow-sm">
                                    <div className="flex justify-between items-center">
                                      <div>
                                        <p className="text-sm font-medium text-gray-900">{reservation.courtName}</p>
                                        <p className="text-sm text-gray-500">{formatDate(reservation.date)}</p>
                                      </div>
                                      <div className="text-right">
                                        <p className="text-sm font-medium text-gray-900">
                                          {formatTime(reservation.startTime)} - {formatTime(reservation.endTime)}
                                        </p>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-gray-500">No upcoming reservations</p>
                            )}
                          </div>

                          {/* Your Registered Events */}
                          <div className="bg-gray-50 p-4 rounded-lg">
                            <h3 className="text-lg font-medium text-gray-900 mb-4">Your Registered Events</h3>
                            {loadingEvents ? (
                              <div className="text-gray-500">Loading events...</div>
                            ) : upcomingEvents.filter(event => event.isCurrentUserSignedUp).length > 0 ? (
                              <div className="space-y-4">
                                {upcomingEvents
                                  .filter(event => event.isCurrentUserSignedUp)
                                  .map((event) => (
                                    <div key={event.id} className="bg-white p-4 rounded-lg shadow-sm">
                                      <div className="flex flex-col">
                                        <div className="flex justify-between items-start">
                                          <div>
                                            <h4 className="text-base font-semibold text-gray-900">{event.title}</h4>
                                            <p className="text-sm text-gray-600">
                                              {format(parseISO(event.date), 'EEEE, MMMM d, yyyy')} • {' '}
                                              {format(new Date(`2000-01-01T${event.startTime}`), 'h:mm a')} - {' '}
                                              {format(new Date(`2000-01-01T${event.endTime}`), 'h:mm a')}
                                            </p>
                                          </div>
                                          <div className="flex items-center space-x-2">
                                            <span className="text-sm text-gray-500">
                                              {event.participants?.length || 0}/{event.capacity || 0} participants
                                            </span>
                                          </div>
                                        </div>
                                        {event.description && (
                                          <p className="mt-2 text-sm text-gray-600">{event.description}</p>
                                        )}
                                      </div>
                                    </div>
                                  ))}
                              </div>
                            ) : (
                              <p className="text-gray-500">You haven't registered for any events yet</p>
                            )}
                          </div>

                          {/* Your Recurring Events */}
                          <div className="bg-gray-50 p-4 rounded-lg mt-6">
                            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                              <RepeatIcon style={{ width: '20px', height: '20px', marginRight: '8px', color: '#9333ea' }} />
                              Your Recurring Events
                            </h3>
                            {loadingRecurringEvents ? (
                              <div className="text-gray-500">Loading recurring events...</div>
                            ) : recurringEvents.filter(event => {
                              // Check if user is signed up for this recurring event
                              return event.participants?.some(p => p.userId === user?.uid);
                            }).length > 0 ? (
                              <div className="space-y-4">
                                {recurringEvents
                                  .filter(event => event.participants?.some(p => p.userId === user?.uid))
                                  .map((event) => (
                                    <div key={event.id} className="bg-white p-4 rounded-lg shadow-sm border-l-4 border-purple-500">
                                      <div className="flex flex-col">
                                        <div className="flex justify-between items-start">
                                          <div>
                                            <div className="flex items-center">
                                              <h4 className="text-base font-semibold text-gray-900">{event.title}</h4>
                                              <span className="ml-2 px-2 py-1 bg-purple-100 text-purple-800 text-xs font-medium rounded-full">
                                                Recurring
                                              </span>
                                            </div>
                                            <p className="text-sm text-gray-600 mt-1">
                                              {format(parseISO(event.startDate), 'MMMM d, yyyy')} • {' '}
                                              {format(new Date(`2000-01-01T${event.startTime}`), 'h:mm a')} - {' '}
                                              {format(new Date(`2000-01-01T${event.endTime}`), 'h:mm a')}
                                            </p>
                                          </div>
                                          <div className="flex items-center space-x-2">
                                            <span className="text-sm text-gray-500">
                                              {event.participants?.length || 0}/{event.capacity || 0} participants
                                            </span>
                                          </div>
                                        </div>
                                        
                                        {event.description && (
                                          <p className="mt-2 text-sm text-gray-600">{event.description}</p>
                                        )}
                                        
                                        <div className="mt-2 px-3 py-2 bg-purple-50 rounded-md">
                                          {renderRecurrenceInfo(event)}
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                              </div>
                            ) : (
                              <p className="text-gray-500">You haven't registered for any recurring events</p>
                            )}
                          </div>
                        </div>

                        {/* Right Column */}
                        <div className="space-y-6">
                          {/* Quick Court Reservation */}
                          <div className="bg-gray-50 p-4 rounded-lg">
                            <h3 className="text-lg font-medium text-gray-900 mb-4">Quick Court Reservation</h3>
                            <div className="mb-4 flex space-x-2">
                              {DURATION_OPTIONS.map(opt => (
                                <button
                                  key={opt.value}
                                  onClick={() => setQuickReserveDuration(opt.value as '30min' | '1hour')}
                                  className={`px-3 py-1 rounded-full border text-sm font-medium focus:outline-none transition-colors duration-150 ${quickReserveDuration === opt.value ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-indigo-50'}`}
                                >
                                  {opt.label}
                                </button>
                              ))}
                            </div>
                            <p className="text-gray-600 mb-4">
                              Book a court for this {getTimeOfDay()}:
                            </p>
                            
                            {loadingSlots ? (
                              <div className="text-gray-500">Loading available slots...</div>
                            ) : availableTimeSlots.filter(isSlotWithinBusinessHours).length > 0 ? (
                              <div className="space-y-3">
                                {availableTimeSlots.filter(isSlotWithinBusinessHours).map((slot, index) => (
                                  <div key={index} className="bg-white p-4 rounded-lg shadow-sm flex justify-between items-center">
                                    <div>
                                      <p className="text-sm font-medium text-gray-900">{slot.courtName}</p>
                                      <p className="text-sm text-gray-500">
                                        {formatDateTime(slot.startTime.split('T')[0], slot.startTime.split('T')[1])}
                                      </p>
                                    </div>
                                    <button
                                      onClick={() => handleQuickReserve(slot)}
                                      className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                    >
                                      Reserve Now
                                    </button>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-gray-500">No available slots for today</p>
                            )}
                          </div>

                          {/* All Upcoming Events */}
                          <div className="bg-gray-50 p-4 rounded-lg">
                            <h3 className="text-lg font-medium text-gray-900 mb-4">All Upcoming Events</h3>
                            {loadingEvents ? (
                              <div className="text-gray-500">Loading events...</div>
                            ) : upcomingEvents.length > 0 ? (
                              <div className="space-y-4">
                                {upcomingEvents.map((event) => (
                                  <div key={event.id} className="bg-white p-4 rounded-lg shadow-sm">
                                    <div className="flex flex-col">
                                      <div className="flex justify-between items-start">
                                        <div>
                                          <h4 className="text-base font-semibold text-gray-900">{event.title}</h4>
                                          <p className="text-sm text-gray-600">
                                            {format(parseISO(event.date), 'EEEE, MMMM d, yyyy')} • {' '}
                                            {format(new Date(`2000-01-01T${event.startTime}`), 'h:mm a')} - {' '}
                                            {format(new Date(`2000-01-01T${event.endTime}`), 'h:mm a')}
                                          </p>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                          <span className="text-sm text-gray-500">
                                            {event.participants?.length || 0}/{event.capacity || 0} participants
                                          </span>
                                        </div>
                                      </div>
                                      {event.description && (
                                        <p className="mt-2 text-sm text-gray-600">{event.description}</p>
                                      )}
                                      <p className="mt-2 text-sm italic text-gray-400">
                                        {event.isCurrentUserSignedUp
                                          ? "You are already signed up for this event!"
                                          : event.participants && event.participants.length >= (event.capacity || 0)
                                          ? "This event is full"
                                          : "Sign up for this event today!"}
                                      </p>
                                    </div>
                                  </div>
                                ))}
                                <div className="mt-4">
                                  <button
                                    onClick={() => navigate('/member/dashboard/events')}
                                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-indigo-600 bg-indigo-50 hover:bg-indigo-100"
                                  >
                                    View All Events
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <p className="text-gray-500">No upcoming events</p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {getActiveSection() === 'subscription' && (
                  <NewMemberSubscription businessId={businessId} facilityId={facilityId} />
                )}

                {getActiveSection() === 'reservations' && facilityId && (
                  <div className="flex-1 flex flex-col">
                    {isPPU && publicFacility ? (
                      <PPU_Reservation 
                        facilityId={facilityId} 
                        businessId={businessId}
                        facility={publicFacility}
                      />
                    ) : (
                      <NewMemberCourtReservations businessId={businessId} facilityId={facilityId} />
                    )}
                  </div>
                )}

                {getActiveSection() === 'events' && businessId && facilityId && (
                  <MemberEvents businessId={businessId} facilityId={facilityId} />
                )}

                {getActiveSection() === 'lessons' && businessId && facilityId && (
                  <MemberLessons businessId={businessId} facilityId={facilityId} />
                )}

                {getActiveSection() === 'stringing' && businessId && facilityId && (
                  <div className="bg-white shadow rounded-lg p-6">
                    <StringingService businessId={businessId} facilityId={facilityId} />
                  </div>
                )}

                {getActiveSection() === 'settings' && businessId && facilityId && (
                  <div className="bg-white shadow rounded-lg p-6">
                    <MemberSettings businessId={businessId} facilityId={facilityId} />
                  </div>
                )}
              </div>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
};

export default MemberDashboard; 